//Helper functions to reduce duplicate code
import { ComposerTranslation } from 'vue-i18n'
import { parse } from 'date-fns/parse'
import { format, parseISO } from 'date-fns'
import { computed, toValue, type Ref } from 'vue'

export function required(t: ComposerTranslation) {
  return [(v: any) => !!v || t('core.required', 'Felt må fylles ut')]
}

export function fnrValideringsRegler(t: ComposerTranslation) {
  return [
    (v: string) =>
      !v ||
      v.length === 11 ||
      t(
        'ungdommer.personopplysninger.personalia.fnrlengdefeil',
        'Fødselsnummer må bestå av 11 siffer'
      )
  ]
}

export function mobilValideringsRegler(t: ComposerTranslation) {
  return [
    (v: any) =>
      !v ||
      v === '' ||
      Number.isInteger(v) ||
      (!isNaN(v) && v.length > 7 && v.length < 13) ||
      t('core.mobilValideringFeilmelding', 'Mobil må bestå av 8 siffer')
  ]
}

export function epostValideringsRegler(t: ComposerTranslation) {
  return [
    (v: any) =>
      !v ||
      v === '' ||
      /.+@.+\..+/.test(v) ||
      t('core.epostValideringFeilmelding', 'Ikke gylding epostadresse')
  ]
}

export function gruppeSorteringValideringsRegler(t: ComposerTranslation) {
  return [
    (v: any) =>
      !v ||
      v === '' ||
      Number.isInteger(v) ||
      !isNaN(v) ||
      t('ungdomgrupper.nyGruppe.regler.sortering', 'Sortering må være et siffer')
  ]
}

export function otUngdomGruppeNavnRegler(t: ComposerTranslation) {
  return [
    (v: any) => {
      if (v) return true

      return t('ungdomgrupper.nyGruppe.regler.gruppenavn.pakrevd', 'Gruppenavn må angis')
    }
  ]
}

export function otMerknadRegler(t: ComposerTranslation) {
  return [
    (v: any) =>
      !v ||
      v.length <= 80 ||
      t(
        'ungdommer.personopplysninger.personalia.otMerknadValideringFeilmelding',
        'Merknad kan max bestå av 80 tegn'
      )
  ]
}

export function notatTittelRegler(t: ComposerTranslation) {
  return [
    (v: any) =>
      !v ||
      v.length <= 40 ||
      t('notater.tittel.maxAntallFeilmelding', 'Emne kan maks bestå av 40 tegn')
  ]
}

const LOCALDATETIME_FORMAT = `yyyy-MM-dd'T'HH:mm:ss`

export function parseLocalDateTime(dateLike: string | Date) {
  if (dateLike instanceof Date) {
    return dateLike
  }

  const parsed =
    dateLike && dateLike.length > 0 ? parse(dateLike, LOCALDATETIME_FORMAT, new Date()) : null
  return parsed
}

export function isRedundantNavigation(currentRoute: string | any, targetRoute: string): boolean {
  return currentRoute === targetRoute
}

export function formatDate(val: string | null | undefined | Date) {
  if (val instanceof Date) {
    return format(val, 'dd.MM.yyyy')
  }

  if (val && val.trim().length === 10) {
    const parsedDate = parse(val.trim(), 'yyyy-MM-dd', new Date())
    return format(parsedDate, 'dd.MM.yyyy')
  } else return ''
}

export function isValidDate(val: string | null | undefined | Date) {
  return toIsoLocalDate(val) != null
}

export function toIsoLocalDate(val: string | null | undefined | Date) {
  if (!val) {
    return null
  }
  if (val instanceof Date) {
    if (isNaN(val.getTime())) {
      return null
    }
    return format(val, 'yyyy-MM-dd')
  }
  val = val.trim()
  if (val.length == 6 && val.match(/[0-9]{6}/g)) {
    const parsedDate = parse(val, 'ddMMyy', new Date())
    return toIsoLocalDate(parsedDate)
  }
  if (val.length != 10) {
    return null
  }
  if (val.match(/[0-9]{4}-[0-9]{2}-[0-9]{2}/g)) {
    return val
  }
  const parsedDate = parse(val, 'dd.MM.yyyy', new Date())
  return toIsoLocalDate(parsedDate)
}

export function formatDateWithHoursMinutes(dateString: string | null | undefined) {
  if (dateString) {
    const date = new Date(dateString)
    return format(date, 'dd.MM.yyyy HH:mm')
  } else return ''
}

export function localDateTime(date: Date): string {
  if (!date || isNaN(date.getTime())) {
    return ''
  }

  return format(date, LOCALDATETIME_FORMAT)
}

export function formatLocalDateTime(date: string) {
  const dateObj = parseISO(date)
  const formattedDate = format(dateObj, 'dd.MM.yyyy')

  return formattedDate
}

export function groupBy<T, K>(iterable: Iterable<T>, fn: (item: T) => K) {
  return [...iterable].reduce<Map<K, T[]>>((map, curr) => {
    const key = fn(curr)
    const list = map.get(key)
    if (list) {
      list.push(curr)
    } else {
      map.set(key, [curr])
    }
    return map
  }, new Map<K, T[]>())
}

export function mapsAreEqual(map1: Map<any, any>, map2: Map<any, any>): boolean {
  if (map1 == map2) {
    return true
  }
  if (map1.size !== map2.size) return false
  const array1 = Array.from(map1.entries())
  for (const entry1 of array1) {
    if (!map2.has(entry1[0])) {
      return false
    }
    if (map2.get(entry1[0]) != entry1[1]) {
      return false
    }
  }
  return true
}

export function sortedArraysAreEqual(a: any[], b: any[]): boolean {
  if (a == b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  return JSON.stringify([...a].sort()) == JSON.stringify([...b].sort())
}

export function formatSkolear(skolear: number | string | null | undefined): string {
  if (!skolear) {
    return ''
  }

  const skolearsString = skolear.toString()

  // Ensure the string is exactly 8 characters (YYYYYYYY format)
  if (skolearsString.length !== 8) {
    return skolearsString
  }

  // Format as YYYY/YYYY
  return skolearsString.slice(0, 4) + '/' + skolearsString.slice(4)
}

/**
 * Composable for calculating dynamic table height based on number of rows
 * @param data - Reactive data array (can be computed or ref)
 * @param options - Configuration options for height calculation
 * @returns Computed height value
 */
export function useDynamicTableHeight<T>(
  data: Ref<T[] | null | undefined> | (() => T[] | null | undefined),
  options: {
    headerHeight?: number
    rowHeight?: number
    minHeight?: number
    maxHeight?: number | Ref<number | undefined>
  } = {}
) {
  const { headerHeight = 56, rowHeight = 52, minHeight = 200 } = options

  return computed(() => {
    // Handle both ref and computed data
    const items = typeof data === 'function' ? data() : data.value

    if (!items) return minHeight

    const calculatedHeight = headerHeight + items.length * rowHeight

    // Return height within bounds
    return Math.max(minHeight, Math.min(calculatedHeight, toValue(options.maxHeight) ?? 800))
  })
}

export function getCurrentYear() {
  return new Date().getFullYear()
}
